import { cellAt } from './board.js';
import { floodRegion } from './floodfill.js';
import { getControlledRegion, isCornerConnected } from './region.js';
import {
  applyInversion,
  applyMapping,
  buildChaosMapping,
  buildInversionMapping,
  effectiveColor,
  flippingPhase,
  isSpecial,
} from './specials.js';
import type { Board, Coord, Effect, GameState, MoveResult, TileType } from './types.js';
import { coordKey } from './types.js';

const REJECTED = (state: GameState): MoveResult => ({ state, effects: [], rejected: true });

const keyToCoord = (k: string): Coord => {
  const [xs, ys] = k.split(',');
  return { x: Number(xs), y: Number(ys) };
};

const collectPenalties = (
  board: Board,
  region: ReadonlySet<string>,
  visited: ReadonlySet<string>,
): { coords: Coord[]; keys: string[] } => {
  const coords: Coord[] = [];
  const keys: string[] = [];
  region.forEach((k) => {
    const c = keyToCoord(k);
    if (cellAt(board, c).tile !== 'penalty') return;
    if (visited.has(k)) return;
    coords.push(c);
    keys.push(k);
  });
  return { coords, keys };
};

const collectSpecials = (
  board: Board,
  region: ReadonlySet<string>,
  visited: ReadonlySet<string>,
): { coord: Coord; tile: TileType }[] => {
  const out: { coord: Coord; tile: TileType }[] = [];
  region.forEach((k) => {
    const c = keyToCoord(k);
    const cell = board.cells[c.y]?.[c.x];
    if (!cell) return;
    if (!isSpecial(cell.tile)) return;
    if (visited.has(k)) return;
    out.push({ coord: c, tile: cell.tile });
  });
  return out;
};

const chaosRng = (): ((n: number) => number) => () => Math.floor(Math.random() * 1e9);

const tileToEffect = (tile: TileType, origin: Coord, board: Board): Effect | null => {
  if (tile === 'rotateL') return { type: 'rotate', degrees: -90 };
  if (tile === 'rotateR') return { type: 'rotate', degrees: 90 };
  if (tile === 'mirrorH') return { type: 'mirror', axis: 'x' };
  if (tile === 'mirrorV') return { type: 'mirror', axis: 'y' };
  if (tile === 'inversion')
    return { type: 'inversion', origin, mapping: buildInversionMapping(board.palette) };
  if (tile === 'chaos') {
    const pick = chaosRng();
    return { type: 'chaos', origin, mapping: buildChaosMapping(board.palette, (n) => pick(n) % n) };
  }
  return null;
};

const finalize = (state: GameState, effects: Effect[]): MoveResult => {
  const won = isCornerConnected(state.board);
  if (won) {
    return {
      state: { ...state, status: 'won' },
      effects: [...effects, { type: 'win' }],
      rejected: false,
    };
  }
  if (state.movesUsed >= state.moveBudget) {
    return {
      state: { ...state, status: 'lost' },
      effects: [...effects, { type: 'lose' }],
      rejected: false,
    };
  }
  return { state, effects, rejected: false };
};

export const applyMove = (state: GameState, target: string): MoveResult => {
  if (state.status !== 'playing') return REJECTED(state);
  if (!state.board.palette.includes(target)) return REJECTED(state);
  const phase = flippingPhase(state.movesUsed);
  const startColor = effectiveColor(cellAt(state.board, state.board.start), phase);
  if (target === startColor) return REJECTED(state);

  const { cells, nextCells } = floodRegion(state.board, target, phase);
  if (cells.length === 0) return REJECTED(state);

  let board: Board = { ...state.board, cells: nextCells };
  const region = getControlledRegion(board, board.start);

  const penalties = collectPenalties(board, region, state.visitedPenalty);
  const visitedPenalty = new Set(state.visitedPenalty);
  penalties.keys.forEach((k) => visitedPenalty.add(k));

  const specials = collectSpecials(board, region, state.visitedSpecial);
  const visitedSpecial = new Set(state.visitedSpecial);

  const effects: Effect[] = [{ type: 'flood', cells, to: target }];
  if (penalties.coords.length > 0) {
    effects.push({
      type: 'penalty',
      cells: penalties.coords,
      movesLost: penalties.coords.length,
    });
  }
  for (const sp of specials) {
    visitedSpecial.add(coordKey(sp.coord));
    const eff = tileToEffect(sp.tile, sp.coord, board);
    if (!eff) continue;
    if (eff.type === 'inversion') {
      board = { ...board, cells: applyInversion(board.cells, eff.mapping) };
    } else if (eff.type === 'chaos') {
      board = { ...board, cells: applyMapping(board.cells, eff.mapping) };
    }
    effects.push(eff);
  }

  const movesUsed = state.movesUsed + 1;
  const moveBudget = state.moveBudget - penalties.coords.length;
  const next: GameState = {
    ...state,
    board,
    movesUsed,
    moveBudget,
    visitedPenalty,
    visitedSpecial,
  };
  return finalize(next, effects);
};
